import React from "react";
import {
  DetailQuantitativeAnalysis,
  QuantitativeAnalysis as ComponentQuantitativeAnalysis,
} from "../../types";
import AnalysisSection from "./components/AnalysisSection";
import AnalysisDetail from "./components/AnalysisDetail";
import {
  Alert,
  AlertDescription,
} from "../../../../components/ui/AlertDetailedReport";

// Example icon imports (replace or remove if you prefer different images)
import detailedAnalysisIcon from "../../../../assets/images/detailed_analysis_purple.svg";
import mathIcon from "../../../../assets/images/calculatel.svg";

interface QuantitativeAnalysisProps {
  data: DetailQuantitativeAnalysis;
  componentAnalysisSection?: ComponentQuantitativeAnalysis;
}

const QuantitativeAnalysis: React.FC<QuantitativeAnalysisProps> = ({
  data,
  componentAnalysisSection,
}) => {
  const { accuracy, insights, speed, answerKey } = data;

  return (
    <div className="space-y-6">
      {/* ======================= Accuracy Section ======================= */}
      <AnalysisSection
        title="Accuracy"
        status={
          accuracy.status ||
          componentAnalysisSection?.criteria.accuracy.status ||
          componentAnalysisSection?.overallStatus
        }
      >
        {/* 1) Detailed analysis */}
        <AnalysisDetail
          icon={<img src={detailedAnalysisIcon} alt="Detailed Analysis" />}
          title="Detailed analysis"
        >
          {accuracy.detailedAnalysis}
        </AnalysisDetail>

        {/* 2) Errors & Corrections side by side (if any) */}
        <div className="grid grid-cols-2 gap-4">
          {accuracy.corrections.length > 0 && (
            <div>
              <h4 className="font-semibold text-[#0EBC8B] mb-2">Corrections</h4>
              <Alert className="!border-0 !shadow-none">
                <AlertDescription className="!text-base text-gray-700">
                  <ul className="list-disc pl-5 space-y-1">
                    {accuracy.corrections.map((correction, idx) => (
                      <li key={idx}>{correction}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            </div>
          )}
          {accuracy.errors.length > 0 && (
            <div>
              <h4 className="font-semibold text-[#EE0000] mb-2">Missed</h4>
              <Alert className="!border-0 !shadow-none">
                <AlertDescription className="!text-base text-gray-700">
                  <ul className="list-disc pl-5 space-y-1 text-gray-700">
                    {accuracy.errors.map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            </div>
          )}
        </div>
      </AnalysisSection>

      {/* ======================= Insights Section ======================= */}
      <AnalysisSection
        title="Insights"
        status={
          insights.status ||
          componentAnalysisSection?.criteria.insights.status ||
          componentAnalysisSection?.overallStatus
        }
      >
        {/* 1) Detailed analysis */}
        <AnalysisDetail
          icon={<img src={detailedAnalysisIcon} alt="Detailed Analysis" />}
          title="Detailed analysis"
        >
          {insights.detailedAnalysis}
        </AnalysisDetail>

        {/* 2) Strong & Missed side by side */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h4 className="font-semibold text-[#0EBC8B] mb-2">Strong</h4>
            <Alert className="!border-0 !shadow-none">
              <AlertDescription className="!text-base text-gray-700">
                <ul className="list-disc pl-5 space-y-1">
                  {insights.examples.strong.map((point, idx) => (
                    <li key={idx}>{point}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          </div>

          <div>
            <h4 className="font-semibold text-[#EE0000] mb-2">Missed</h4>
            <Alert className="!border-0 !shadow-none">
              <AlertDescription className="!text-base text-gray-700">
                <ul className="list-disc pl-5 space-y-1">
                  {insights.examples.missed.map((point, idx) => (
                    <li key={idx}>{point}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          </div>
        </div>
      </AnalysisSection>

      {/* ======================= Speed Section ======================= */}
      <AnalysisSection
        title="Speed"
        status={
          speed.status ||
          componentAnalysisSection?.criteria.speed.status ||
          componentAnalysisSection?.overallStatus
        }
      >
        <AnalysisDetail
          icon={<img src={detailedAnalysisIcon} alt="Detailed Analysis" />}
          title="Detailed analysis"
        >
          {speed.detailedAnalysis}
        </AnalysisDetail>
      </AnalysisSection>

      {/* ======================= Answer Key Section ======================= */}
      <AnalysisSection title="Answer Key">
        <AnalysisDetail
          icon={<img src={mathIcon} alt="Math" />}
          title="Calculations"
          className="!border-0 !shadow-none"
        >
          {/* We wrap the answerKey or parse it however you'd like */}
          <p className="!text-base text-gray-700 leading-relaxed">
            {answerKey}
          </p>
        </AnalysisDetail>
      </AnalysisSection>
    </div>
  );
};

export default QuantitativeAnalysis;
